import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDownIcon } from '../Icons';
import Typography from '../Typography';
import AvatarText from './AvatarText';
import './AvatarTextItem.scss';

const AvatarTextItem = ({
  id,
  avatar = '',
  fallback = '',
  title = '',
  subtitle = '',
  size = 'md',
  shape = 'circle',
  fallbackVariant = 'default',
  alt = '',
  active = false,
  onClick,
  className = '',
  leadingIcon = null,
  trailingContent = null,
  expandable = false,
  expanded,
  defaultExpanded = false,
  onToggle,
  radius = 'rounded',
  showActiveIndicator = false,
  hideBottomBorder = false,
  children = null,
  withFrame = false,
}) => {
  const hasAvatar = Boolean(avatar || fallback);
  const isControlled = typeof expanded === 'boolean';
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;

  const itemClass = ['ds-avatar-text-item', className].filter(Boolean).join(' ');
  const triggerClass = [
    'ds-avatar-text-item__trigger',
    radius === 'flush' ? 'ds-avatar-text-item__trigger--flush' : 'ds-avatar-text-item__trigger--rounded',
    active && 'ds-avatar-text-item__trigger--active',
    active && showActiveIndicator && 'ds-avatar-text-item__trigger--active-indicator',
  ]
    .filter(Boolean)
    .join(' ');

  const handleTriggerClick = (event) => {
    onClick?.(event);
    if (expandable) {
      const nextExpanded = !isExpanded;
      if (!isControlled) {
        setInternalExpanded(nextExpanded);
      }
      onToggle?.(nextExpanded, event);
    }
  };

  const interactive = onClick || expandable;
  const TriggerTag = interactive ? 'button' : 'div';
  const triggerStyle = hideBottomBorder ? { borderBottom: 'none' } : undefined;
  const triggerProps = interactive
    ? {
        id,
        type: 'button',
        onClick: handleTriggerClick,
        'aria-pressed': active || undefined,
        'aria-expanded': expandable ? isExpanded : undefined,
      }
    : { id };
  const chevronClassName = ['ds-avatar-text-item__chevron', isExpanded && 'ds-avatar-text-item__chevron--expanded']
    .filter(Boolean)
    .join(' ');
  const trailingNode =
    trailingContent || (expandable ? <ChevronDownIcon size={16} className={chevronClassName} /> : null);

  return (
    <div className={itemClass}>
      <TriggerTag className={triggerClass} style={triggerStyle} {...triggerProps}>
        <div className="ds-avatar-text-item__content">
          {hasAvatar ? (
            <AvatarText
              avatar={avatar}
              fallback={fallback}
              title={title}
              subtitle={subtitle}
              size={size}
              shape={shape}
              fallbackVariant={fallbackVariant}
              alt={alt}
              withFrame={withFrame}
            />
          ) : (
            <>
              {leadingIcon ? <span className="ds-avatar-text-item__leading-icon">{leadingIcon}</span> : null}
              <div className="ds-avatar-text-item__text">
                <Typography variant="bodyMedium" component="p" className="ds-avatar-text-item__title">
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography variant="bodySmall" component="p" className="ds-avatar-text-item__subtitle">
                    {subtitle}
                  </Typography>
                ) : null}
              </div>
            </>
          )}
        </div>

        {(trailingContent || expandable) && <span className="ds-avatar-text-item__trailing">{trailingNode}</span>}
      </TriggerTag>
      {expandable && isExpanded && children ? <div className="ds-avatar-text-item__panel">{children}</div> : null}
    </div>
  );
};

AvatarTextItem.propTypes = {
  id: PropTypes.string,
  avatar: PropTypes.string,
  fallback: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  shape: PropTypes.oneOf(['circle', 'square']),
  fallbackVariant: PropTypes.oneOf(['default', 'theme']),
  alt: PropTypes.string,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  leadingIcon: PropTypes.node,
  trailingContent: PropTypes.node,
  expandable: PropTypes.bool,
  expanded: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  radius: PropTypes.oneOf(['rounded', 'flush']),
  showActiveIndicator: PropTypes.bool,
  hideBottomBorder: PropTypes.bool,
  children: PropTypes.node,
  withFrame: PropTypes.bool,
};

AvatarTextItem.defaultProps = {
  id: undefined,
  avatar: '',
  fallback: '',
  title: '',
  subtitle: '',
  size: 'md',
  shape: 'circle',
  fallbackVariant: 'default',
  alt: '',
  active: false,
  onClick: undefined,
  className: '',
  leadingIcon: null,
  trailingContent: null,
  expandable: false,
  expanded: undefined,
  defaultExpanded: false,
  onToggle: undefined,
  radius: 'rounded',
  showActiveIndicator: false,
  hideBottomBorder: false,
  children: null,
  withFrame: false,
};

export default AvatarTextItem;
